import React, { useContext, useState } from "react"
import { Link, Redirect } from "react-router-dom"
import { ReactComponent as BellIcon } from "../../../assets/images/bell.svg"
import { ReactComponent as TimeIcon } from "../../../assets/images/time.svg"
import { ReactComponent as MainIcon } from "../../../assets/images/main.svg"
import { ReactComponent as MusicIcon } from "../../../assets/images/music.svg"
import { ReactComponent as TemplatesIcon } from "../../../assets/images/templates.svg"
import { ReactComponent as WifiIcon } from "../../../assets/images/wifi.svg"
import { ReactComponent as ChannelsIcon } from "../../../assets/images/channels.svg"
import { ReactComponent as LockIcon } from "../../../assets/images/lock.svg"
import { StateContext } from "../../../context/StateContext"
import { getRealFlags } from "../../../utils/realFlags"

import Logs from "./Logs"

import ImpactForce from "./ImpactForce"
import ImpactDuration from "./ImpactDuration"
import Channels from "./Channels"
import General from "./General"
import Bells from "./Bells"
import Header from "../../../components/header/header"
import Constructor from "./Constructor"
import Melodies from "./Melodies"
import Remote from "../../../fsd/features/Settings/Remote/Remote"
import Templates from "../../../fsd/features/Settings/Templates/Templates"

const SettingLink = ({ to, disabled, children }) => {
  if (disabled) {
    return <div className="setting-link setting-link--disabled">{children}</div>
  }

  return (
    <Link to={to} className="setting-link">
      {children}
    </Link>
  )
}

const PACKET2_TABS = ["force", "duration", "general"]
const PACKET3_TABS = ["channels", "remote", "templates"]
const LOCKED_TABS = ["force", "duration"]
const LOCK_STORAGE_KEY = "settingsUnlockedAt"
const LOCK_TTL_MS = 24 * 60 * 60 * 1000
const LOCK_PASSWORD_HASH =
  "03ac674216f3e15c761ee1a5e255f067953623c8b388b4459e13f978d7c846f4"

const hashPassword = async (value) => {
  const buffer = await window.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(value)
  )

  return Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("")
}

const isForceDurationUnlocked = () => {
  const unlockedAt = localStorage.getItem(LOCK_STORAGE_KEY)

  if (!unlockedAt) {
    return false
  }

  const unlockedAtMs = Date.parse(unlockedAt)

  if (Number.isNaN(unlockedAtMs) || Date.now() - unlockedAtMs >= LOCK_TTL_MS) {
    localStorage.removeItem(LOCK_STORAGE_KEY)
    return false
  }

  return true
}

const Settings = ({ tab }) => {
  const STATE = useContext(StateContext)
  const { version } = STATE.lastData.settings
  const { is2Real, is3Real } = getRealFlags(STATE.lastData)
  const [isUnlocked, setIsUnlocked] = useState(isForceDurationUnlocked)

  const askLockPassword = async () => {
    while (true) {
      const password = window.prompt("Введите пароль")

      if (password === null) {
        return
      }

      const passwordHash = await hashPassword(password)

      if (passwordHash === LOCK_PASSWORD_HASH) {
        localStorage.setItem(LOCK_STORAGE_KEY, new Date().toISOString())
        setIsUnlocked(true)
        return
      }
    }
  }

  if (PACKET2_TABS.includes(tab) && !is2Real) {
    return <Redirect to={`/device/${STATE.subscribed.id}/settings`} />
  }

  if (LOCKED_TABS.includes(tab) && !isUnlocked) {
    return <Redirect to={`/device/${STATE.subscribed.id}/settings`} />
  }

  if (PACKET3_TABS.includes(tab) && !is3Real) {
    return <Redirect to={`/device/${STATE.subscribed.id}/settings`} />
  }

  if (typeof tab === "undefined")
    return (
      <div className="main-area-field full-height">
        <div className="main-area full-height">
          <div className="page-area no-p">
            <Header
              title={"Настройки"}
              backTitle="Пульт"
              backUrl={`/device/${STATE.subscribed.id}`}
            />

            <Logs />

            <div className="content">
              <div className="block setting-block">
                <div className="settings-lock-wrap">
                  <SettingLink
                    to={`/device/${STATE.subscribed.id}/settings/force`}
                    disabled={!is2Real}
                  >
                    <div className="icon-area">
                      <BellIcon className="setting-icon" />
                    </div>

                    <div className="setting-text">Сила удара</div>
                  </SettingLink>

                  <SettingLink
                    to={`/device/${STATE.subscribed.id}/settings/duration`}
                    disabled={!is2Real}
                  >
                    <div className="icon-area">
                      <TimeIcon className="setting-icon icon-time" />
                    </div>
                    <div className="setting-text">Задержка</div>
                  </SettingLink>

                  {!isUnlocked && (
                    <button
                      type="button"
                      className="settings-lock-banner"
                      onClick={askLockPassword}
                    >
                      <LockIcon className="settings-lock-icon" />
                    </button>
                  )}
                </div>

                {STATE.lastData.channels && (
                  <SettingLink
                    to={`/device/${STATE.subscribed.id}/settings/channels`}
                    disabled={!is3Real}
                  >
                    <div className="icon-area">
                      <ChannelsIcon className="setting-icon setting-icon--channels" />
                    </div>
                    <div className="setting-text">Каналы</div>
                  </SettingLink>
                )}

                {version === 3.6 && (
                  <Link
                    to={`/device/${STATE.subscribed.id}/settings/constructor`}
                    className="setting-link"
                  >
                    <div
                      className="icon-area"
                      style={{ marginLeft: "-3px", marginRight: "13px" }}
                    >
                      <MusicIcon className="setting-icon" />
                    </div>
                    <div className="setting-text">Конструктор звонов</div>
                  </Link>
                )}

                <Link
                  to={`/device/${STATE.subscribed.id}/settings/melodies`}
                  className="setting-link"
                >
                  <div
                    className="icon-area"
                    style={{ marginLeft: "-3px", marginRight: "13px" }}
                  >
                    <MusicIcon className="setting-icon" />
                  </div>
                  <div className="setting-text">Названия звонов</div>
                </Link>

                {STATE.lastData.templates && (
                  <SettingLink
                    to={`/device/${STATE.subscribed.id}/settings/remote`}
                    disabled={!is3Real}
                  >
                    <div className="icon-area">
                      <WifiIcon className="setting-icon setting-icon--wifi" />
                    </div>
                    <div className="setting-text">Радиопульт</div>
                  </SettingLink>
                )}

                {STATE.lastData.templates && (
                    <SettingLink
                      to={`/device/${STATE.subscribed.id}/settings/templates`}
                      disabled={!is3Real}
                    >
                      <div className="icon-area">
                        <TemplatesIcon className="setting-icon setting-icon--templates" />
                      </div>
                      <div className="setting-text">Программы</div>
                    </SettingLink>
                )}

                <SettingLink
                  to={`/device/${STATE.subscribed.id}/settings/general`}
                  disabled={!is2Real}
                >
                  <div className="icon-area">
                    <MainIcon className="setting-icon" />
                  </div>
                  <div className="setting-text">Основные</div>
                </SettingLink>

                {(
                  !!STATE.lastData.files
                  && STATE.lastData.files.length > 0
                ) && (
                  <Link
                    to={`/device/${STATE.subscribed.id}/settings/bells`}
                    className="setting-link"
                  >
                    <div className="icon-area">
                      <BellIcon className="setting-icon" />
                    </div>
                    <div className="setting-text">Звоны на устройстве</div>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )

  if (tab === "force") return <ImpactForce />
  if (tab === "duration") return <ImpactDuration />
  if (tab === "channels") return <Channels />
  if (tab === "general") return <General />
  if (tab === "bells") return <Bells />
  if (tab === "constructor") return <Constructor />
  if (tab === "melodies") return <Melodies />
  if (tab === "remote") return <Remote />
  if (tab === "templates") return <Templates />
}

export default Settings
